(() => {
  const element = document.getElementById('funto-paper-gift-config');
  if (!element || window.funtoPaperGiftsLoaded) return;
  window.funtoPaperGiftsLoaded = true;
  const config = JSON.parse(element.textContent);
  const eligible = new Map(config.eligible.map(item => [Number(item.id), item]));
  const nativeFetch = window.fetch.bind(window);
  const marker = 'paper-gift-20261031';
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  let queue = Promise.resolve();
  let pending = 0;
  let retryCheckout = false;
  const managed = item => item.properties?._funto_paper_gift === marker;
  const message = failed => {
    const node = document.querySelector('[data-funto-paper-status]');
    if (node) {
      node.hidden = !failed;
      node.textContent = failed ? config.unavailable : '';
    }
  };
  function exchangeRate(cart) {
    if (cart.currency === config.shopCurrency) return 1;
    const rate = Number(window.Shopify?.currency?.rate);
    if (Number.isFinite(rate) && rate > 0) return rate;
    const item = cart.items.find(line => eligible.has(Number(line.variant_id)) && line.original_price > 0);
    return item ? item.original_price / eligible.get(Number(item.variant_id)).usdCents : null;
  }
  function giftFor(cart) {
    const rate = exchangeRate(cart);
    if (!rate) return null;
    const amount = cart.items.reduce((total, item) => {
      if (managed(item) || !eligible.has(Number(item.variant_id))) return total;
      if (item.line_level_discount_allocations?.some(allocation => allocation.amount > 0)) return total;
      return total + item.original_line_price;
    }, 0);
    const target = [...config.tiers].reverse().find(tier => amount >= Math.round(tier.threshold * rate));
    return target?.available ? target : null;
  }
  async function readCart() {
    const response = await nativeFetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart read failed');
    return response.json();
  }
  async function writeCart(path, body) {
    const response = await nativeFetch(`${root}${path}`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Gift update failed');
    return response.json();
  }
  async function reconcile(cart) {
    let changed = false;
    const target = giftFor(cart);
    const updates = {};
    let kept = false;
    const existingFree = target && cart.items.some(item => !managed(item) && Number(item.variant_id) === target.id && item.final_line_price === 0);
    for (const item of cart.items.filter(managed)) {
      if (target && !existingFree && Number(item.variant_id) === target.id && !kept) {
        kept = true;
        if (item.quantity !== 1) updates[item.key] = 1;
      } else updates[item.key] = 0;
    }
    if (Object.keys(updates).length) {
      cart = await writeCart('cart/update.js', { updates });
      changed = true;
    }
    if (target && !kept && !existingFree) {
      try {
        await writeCart('cart/add.js', { items: [{ id: target.id, quantity: 1, properties: { _funto_paper_gift: marker } }] });
        cart = await readCart();
        changed = true;
      } catch (error) {
        message(true);
        return { cart, changed };
      }
    }
    const chargedGifts = cart.items.filter(item => managed(item) && item.final_line_price !== 0);
    if (chargedGifts.length) {
      const remove = Object.fromEntries(chargedGifts.map(item => [item.key, 0]));
      cart = await writeCart('cart/update.js', { updates: remove });
      changed = true;
      message(true);
    } else message(false);
    return { cart, changed };
  }
  async function sectionsFor(names) {
    if (!names.length) return {};
    const url = new URL(window.location.href);
    url.searchParams.set('sections', names.slice(0, 5).join(','));
    const response = await nativeFetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart sections read failed');
    return response.json();
  }
  function enqueue(task) {
    pending++;
    const result = queue.then(task);
    queue = result.catch(() => {}).finally(() => { pending--; });
    return result;
  }
  window.fetch = function(input, init) {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.origin); }
    catch { return nativeFetch(input, init); }
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== window.location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) return nativeFetch(input, init);
    return enqueue(async () => {
      const response = await nativeFetch(input, init);
      if (!response.ok) return response;
      let body;
      try { body = await response.clone().json(); } catch { return response; }
      try {
        const result = await reconcile(await readCart());
        if (!result.changed) return response;
        const names = Object.keys(body.sections || {});
        const sections = names.length ? await sectionsFor(names) : undefined;
        const payload = Array.isArray(body.items) && Object.hasOwn(body, 'total_price') ? { ...result.cart } : { ...body };
        if (sections) payload.sections = sections;
        const headers = new Headers(response.headers);
        headers.delete('content-length'); headers.delete('content-encoding');
        return new Response(JSON.stringify(payload), { status: response.status, statusText: response.statusText, headers });
      } catch (error) {
        message(true);
        throw error;
      }
    });
  };
  const paperTranslations = {
    en:['Type a number or use + / −','Your free gift','One gift at the highest eligible tier.','Add {amount} more in paper to unlock the next gift.','You have reached the highest gift tier.','This option is not part of the paper gift offer.'],
    de:['Zahl eingeben oder + / − nutzen','Dein Gratisgeschenk','Ein Geschenk der höchsten erreichten Stufe.','Noch {amount} Papier bis zum nächsten Geschenk.','Höchste Geschenkstufe erreicht.','Diese Option nimmt nicht an der Papieraktion teil.'],
    fr:['Saisissez un nombre ou utilisez + / −','Votre cadeau offert','Un cadeau du palier le plus élevé atteint.','Ajoutez {amount} de papier pour le prochain cadeau.','Palier cadeau le plus élevé atteint.','Cette option est exclue de cette offre.'],
    es:['Introduce un número o usa + / −','Tu regalo','Un regalo del nivel más alto alcanzado.','Añade {amount} en papel para el siguiente regalo.','Has alcanzado el nivel máximo.','Esta opción no participa en la oferta.'],
    it:['Inserisci un numero o usa + / −','Il tuo omaggio','Un omaggio della fascia più alta raggiunta.','Aggiungi {amount} di carta per il prossimo omaggio.','Hai raggiunto la fascia più alta.','Questa opzione non partecipa alla promozione.'],
    nl:['Voer een aantal in of gebruik + / −','Je gratis cadeau','Eén cadeau uit de hoogste bereikte categorie.','Nog {amount} aan papier voor het volgende cadeau.','Je hebt de hoogste categorie bereikt.','Deze optie valt buiten de papieractie.'],
    pt:['Introduza um número ou use + / −','A sua oferta','Uma oferta do nível mais alto atingido.','Adicione {amount} em papel para a próxima oferta.','Atingiu o nível mais alto.','Esta opção não participa na promoção.'],
    sv:['Ange ett antal eller använd + / −','Din gåva','En gåva från den högsta uppnådda nivån.','Lägg till papper för {amount} för nästa gåva.','Du har nått den högsta nivån.','Detta alternativ ingår inte i erbjudandet.'],
    da:['Indtast et antal eller brug + / −','Din gratis gave','Én gave fra det højeste opnåede niveau.','Tilføj papir for {amount} for næste gave.','Du har nået det højeste niveau.','Denne mulighed er ikke med i tilbuddet.'],
    fi:['Syötä määrä tai käytä + / −','Ilmainen lahjasi','Yksi lahja korkeimmalta saavutetulta tasolta.','Lisää paperia {amount}, niin saat seuraavan lahjan.','Olet saavuttanut korkeimman tason.','Tämä vaihtoehto ei kuulu tarjoukseen.'],
    nb:['Skriv inn antall eller bruk + / −','Din gratisgave','Én gave fra høyeste oppnådde nivå.','Legg til papir for {amount} for neste gave.','Du har nådd høyeste nivå.','Dette alternativet er ikke med i tilbudet.'],
    pl:['Wpisz liczbę lub użyj + / −','Twój gratis','Jeden prezent z najwyższego osiągniętego progu.','Dodaj papier za {amount}, aby otrzymać kolejny prezent.','Osiągnięto najwyższy próg.','Ta opcja nie jest objęta promocją.'],
    cs:['Zadejte počet nebo použijte + / −','Váš dárek zdarma','Jeden dárek z nejvyšší dosažené úrovně.','Přidejte papír za {amount} pro další dárek.','Dosáhli jste nejvyšší úrovně.','Tato možnost není součástí nabídky.'],
    sk:['Zadajte počet alebo použite + / −','Váš darček zadarmo','Jeden darček z najvyššej dosiahnutej úrovne.','Pridajte papier za {amount} pre ďalší darček.','Dosiahli ste najvyššiu úroveň.','Táto možnosť nie je súčasťou ponuky.'],
    el:['Εισαγάγετε ποσότητα ή χρησιμοποιήστε + / −','Το δωρεάν δώρο σας','Ένα δώρο από την υψηλότερη βαθμίδα που φτάσατε.','Προσθέστε χαρτί αξίας {amount} για το επόμενο δώρο.','Φτάσατε στην υψηλότερη βαθμίδα.','Αυτή η επιλογή δεν συμμετέχει στην προσφορά.'],
    ja:['数量を入力するか + / − を使用','無料ギフト','達成した最高金額の特典を1点プレゼント。','対象の紙製品をあと{amount}追加すると次の特典を獲得できます。','最高額の特典に達しました。','このオプションは紙製品の特典対象外です。'],
    ar:['أدخل الكمية أو استخدم + / −','هديتك المجانية','هدية واحدة من أعلى فئة مؤهلة.','أضف منتجات ورقية بقيمة {amount} للحصول على الهدية التالية.','لقد وصلت إلى أعلى فئة للهدايا.','هذا الخيار غير مشمول بعرض الهدايا.']
  };
  const strings = paperTranslations[(document.documentElement.lang || 'en').split('-')[0]] || paperTranslations.en;
  const paperCopy = Object.fromEntries(['quantity','heading','one','next','highest','ineligible'].map((key,index)=>[key,strings[index]]));
  function prepareQuantities(normalize = false) {
    document.querySelectorAll('[data-funto-paper-quantity]').forEach(container => {
      const input = container.querySelector('input[name="quantity"]');
      const data = container.querySelector('[data-funto-quantity-variants]');
      const form = document.getElementById('product-form-' + container.dataset.section);
      if(!input || !data || !form) return;
      const variants = container.funtoVariants ||= JSON.parse(data.textContent);
      const variant = variants.find(v=>String(v.id)===form.querySelector('input[name="id"]')?.value);
      if(!variant) return;
      const changed = container.dataset.variantId !== String(variant.id);
      const min = Math.max(1, Number(variant.min) || 1), step = Math.max(1, Number(variant.step) || 1);
      const remaining = variant.stock == null ? Infinity : Math.max(0,variant.stock - (Number(input.dataset.cartQuantity)||variant.cart||0));
      const max = Math.min(remaining, variant.max == null ? Infinity : Number(variant.max));
      input.min = String(min); input.step = String(step); input.dataset.min = String(min);
      if(Number.isFinite(max)) {input.max = String(max); input.dataset.max = String(max)}
      else {input.removeAttribute('max');delete input.dataset.max}
      if(changed || normalize) {
        let value = Math.max(min,Math.floor(Number(input.value)||min));
        value = min + Math.floor((value-min)/step)*step;
        if(max >= min) value = Math.min(value,min+Math.floor((max-min)/step)*step);
        if(input.value !== String(value)) {input.value=String(value);input.dispatchEvent(new Event('input',{bubbles:true}))}
      }
      container.dataset.variantId = String(variant.id);
      container.querySelectorAll('.funto-paper-extra-row').forEach(row=>{
        const extra=variants.find(v=>String(v.id)===row.querySelector('[data-funto-extra-variant]').value);
        const count=row.querySelector('[data-funto-extra-count]');
        if(!extra)return;
        count.min=String(Math.max(1,extra.min||1));count.step=String(Math.max(1,extra.step||1));
        const max=Math.min(extra.max==null?Infinity:extra.max,extra.stock==null?Infinity:Math.max(0,extra.stock-extra.cart));
        if(Number.isFinite(max))count.max=String(max);else count.removeAttribute('max');
        if(normalize){let value=Math.max(Number(count.min),Math.floor(Number(count.value)||1));value=Number(count.min)+Math.floor((value-Number(count.min))/Number(count.step))*Number(count.step);if(max>=Number(count.min))value=Math.min(value,max);count.value=String(value)}
      });
      const extraLabels = {
        en:['Add another option','Remove option'],de:['Weitere Variante hinzufügen','Variante entfernen'],fr:['Ajouter une autre option','Supprimer cette option'],es:['Añadir otra opción','Eliminar opción'],it:['Aggiungi un’altra opzione','Rimuovi opzione'],nl:['Andere optie toevoegen','Optie verwijderen'],pt:['Adicionar outra opção','Remover opção'],sv:['Lägg till ett alternativ','Ta bort alternativ'],da:['Tilføj en anden variant','Fjern variant'],fi:['Lisää toinen vaihtoehto','Poista vaihtoehto'],nb:['Legg til et alternativ','Fjern alternativ'],pl:['Dodaj inny wariant','Usuń wariant'],cs:['Přidat další variantu','Odebrat variantu'],sk:['Pridať ďalší variant','Odstrániť variant'],el:['Προσθήκη άλλης επιλογής','Αφαίρεση επιλογής'],ja:['別の色・サイズを追加','このオプションを削除'],ar:['إضافة خيار آخر','إزالة الخيار']
      };
      const labels=extraLabels[(document.documentElement.lang||'en').split('-')[0]]||extraLabels.en;
      const add=container.querySelector('[data-funto-add-option]');if(add&&add.textContent!==labels[0])add.textContent=labels[0];
      container.querySelectorAll('[data-funto-remove-option]').forEach(button=>{if(button.getAttribute('aria-label')!==labels[1])button.setAttribute('aria-label',labels[1])});
      const hint = container.querySelector('[data-funto-quantity-hint]');
      if(hint && hint.textContent !== paperCopy.quantity) hint.textContent=paperCopy.quantity;
    });
    document.querySelectorAll('[data-funto-gift-heading]').forEach(node=>{if(node.textContent!==paperCopy.heading)node.textContent=paperCopy.heading});
    document.querySelectorAll('[data-funto-one-gift]').forEach(node=>{if(node.textContent!==paperCopy.one)node.textContent=paperCopy.one});
  }
  function updateOffers() {
    prepareQuantities();
    document.querySelectorAll('[data-funto-paper-gifts]').forEach(gifts => {
      // Shrine's cart calls this interface even for the scoped, non-native gift UI.
      gifts.initUnlock = updateOffers;
      const section = gifts.dataset.section;
      const quantities = document.getElementById('quantity-breaks-' + section);
      const form = document.getElementById('product-form-' + section);
      const selectedId = Number(form?.querySelector('input[name="id"]')?.value);
      const quantityInput = document.getElementById('Quantity-' + section);
      const quantity = Math.max(1, Math.floor(Number(quantityInput?.value) || 1));
      let amount = (eligible.get(selectedId)?.usdCents || 0) * quantity;
      // Preserve compatibility while other instances still render quantity offers.
      quantities?.querySelectorAll('input[type="radio"][name="quantity"]:checked').forEach(radio => {
        const offer = quantities.querySelector('label[for="' + radio.id + '"]');
        const selectors = offer ? [...offer.querySelectorAll('[data-selected-id]')] : [];
        const count = Number(radio.value || 1);
        amount = selectors.length === count
          ? selectors.reduce((sum, node) => sum + (eligible.get(Number(node.dataset.selectedId))?.usdCents || 0), 0)
          : (eligible.get(selectedId)?.usdCents || 0) * count;
      });
      document.querySelectorAll('[data-funto-paper-quantity][data-section="' + section + '"] .funto-paper-extra-row').forEach(row=>{
        const id=Number(row.querySelector('[data-funto-extra-variant]').value);
        const count=Math.max(1,Math.floor(Number(row.querySelector('[data-funto-extra-count]').value)||1));
        amount+=(eligible.get(id)?.usdCents||0)*count;
      });
      const next = config.tiers.find(tier => tier.threshold > amount);
      const hint = document.querySelector('[data-funto-next-gift][data-section="' + section + '"]');
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency }).format(cents * (config.currency === config.shopCurrency ? 1 : rate) / 100);
      const hintText = !eligible.has(selectedId) && amount === 0 ? paperCopy.ineligible : next ? paperCopy.next.replace('{amount}', money(next.threshold - amount)) : paperCopy.highest;
      if(hint && hint.textContent !== hintText) hint.textContent = hintText;
      const target = [...config.tiers].reverse().find(tier => amount >= tier.threshold);
      gifts.querySelectorAll('[data-funto-gift-id]').forEach(card => {
        const id = Number(card.dataset.funtoGiftId);
        const gift = config.tiers.find(tier => tier.id === id);
        const replaced = target?.available && gift && target.threshold > gift.threshold;
        const unlocked = Boolean(target?.available && (target.id === id || replaced));
        card.classList.toggle('quantity-gift--unlocked', unlocked);
        card.classList.toggle('funto-gift--replaced', Boolean(replaced));
        const state = String(unlocked);
        if (card.dataset.unlocked !== state) card.dataset.unlocked = state;
        const badge = card.querySelector('.quantity-gift__badge > span');
        const badgeText = replaced ? '→' : config.free;
        if (badge && badge.textContent !== badgeText) badge.textContent = badgeText;
        const title = card.querySelector('.quantity-gift__title');
        const titleText = gift?.title || '';
        if (title && title.textContent !== titleText) title.textContent = titleText;
      });
    });
  }
  let offerFrame;
  const scheduleOffers = () => {
    if (offerFrame) return;
    offerFrame = requestAnimationFrame(() => { offerFrame = 0; updateOffers(); });
  };
  document.addEventListener('change', event => {
    if(event.target.closest('[data-funto-paper-quantity]')) prepareQuantities(true);
    scheduleOffers();
  });
  document.addEventListener('input', scheduleOffers);
  document.addEventListener('click', scheduleOffers);
  const observer = new MutationObserver(records => {
    if (records.some(record => !record.target.closest?.('[data-funto-paper-offer]') && !record.target.closest?.('[data-funto-paper-status]'))) scheduleOffers();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'data-selected-id'] });
  document.addEventListener('click', async event => {
    const button = event.target.closest('button[name="checkout"],input[name="checkout"],a[href^="/checkout"],a[href*="/checkout?"]');
    if (!button || retryCheckout) return;
    const terms = document.querySelector('[id^="TNC-Checkbox-"]');
    if (terms && terms.dataset.checked !== 'true') return;
    event.preventDefault(); event.stopImmediatePropagation();
    try {
      await enqueue(async () => reconcile(await readCart()));
      retryCheckout = true;
      window.location.assign(button.href || `${root}checkout`);
    } catch { message(true); }
    finally { retryCheckout = false; }
  }, true);
  document.addEventListener('click', event => {
    if(event.target.closest('[id^="ProductSubmitButton-"]') && event.target.closest('[id^="MainProduct-"]')?.querySelector('[data-funto-paper-quantity]')) prepareQuantities(true);
  }, true);
  document.addEventListener('click',event=>{
    const container=event.target.closest('[data-funto-paper-quantity]');if(!container)return;
    const row=event.target.closest('.funto-paper-extra-row');
    if(event.target.closest('[data-funto-add-option]')){
      const content=container.querySelector('[data-funto-extra-template]').content.cloneNode(true);
      const select=content.querySelector('select');
      const used=new Set([container.dataset.variantId,...[...container.querySelectorAll('[data-funto-extra-variant]')].map(s=>s.value)]);
      const option=[...select.options].find(o=>!o.disabled&&!used.has(o.value))||[...select.options].find(o=>!o.disabled);
      if(!option)return;select.value=option.value;
      container.querySelector('[data-funto-extra-options]').append(content);
    }else if(event.target.closest('[data-funto-remove-option]'))row?.remove();
    else if(row&&event.target.closest('[data-funto-extra-plus],[data-funto-extra-minus]')){
      const input=row.querySelector('[data-funto-extra-count]');
      const delta=event.target.closest('[data-funto-extra-plus]')?1:-1;
      const value=(Number(input.value)||1)+delta*(Number(input.step)||1);
      if(value>=Number(input.min)&&(!input.hasAttribute('max')||value<=Number(input.max)))input.value=String(value);
    }else return;
    prepareQuantities(true);container.dispatchEvent(new Event('input',{bubbles:true}));scheduleOffers();
  });
  document.addEventListener('submit',async event=>{
    const form=event.target;
    const section=form.id?.replace(/^product-form-/,'');
    if(!section||form.id!=='product-form-'+section)return;
    const container=document.querySelector('[data-funto-paper-quantity][data-section="'+section+'"]');
    if(!container)return;
    event.preventDefault();event.stopImmediatePropagation();
    const button=document.getElementById('ProductSubmitButton-'+section);
    if(!button||button.disabled||button.classList.contains('loading'))return;
    prepareQuantities(true);
    const inputs=[...container.querySelectorAll('input[type="number"]')];
    if(inputs.some(input=>!input.reportValidity()))return;
    const id=Number(form.querySelector('input[name="id"]').value);
    const lines=[{id,quantity:Number(document.getElementById('Quantity-'+section).value)}];
    container.querySelectorAll('.funto-paper-extra-row').forEach(row=>lines.push({id:Number(row.querySelector('[data-funto-extra-variant]').value),quantity:Number(row.querySelector('[data-funto-extra-count]').value)}));
    const merged=new Map();lines.forEach(line=>merged.set(line.id,(merged.get(line.id)||0)+line.quantity));
    const items=[...merged].map(([id,quantity])=>({id,quantity}));
    const error=container.querySelector('[data-funto-paper-error]');error.hidden=true;
    button.disabled=true;button.classList.add('loading');button.setAttribute('aria-disabled','true');
    try{
      const drawer=document.querySelector('cart-drawer')||document.querySelector('cart-notification');
      const sections=drawer?.getSectionsToRender?.().map(s=>s.id)||[];
      const response=await window.fetch(root+'cart/add.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections,sections_url:window.location.pathname})});
      const result=await response.json();
      if(!response.ok||result.status)throw new Error(result.description||result.message||config.unavailable);
      const cart=await readCart();
      if(typeof publish==='function'&&typeof PUB_SUB_EVENTS!=='undefined')publish(PUB_SUB_EVENTS.cartUpdate,{source:'funto-paper-composer',cartData:cart});
      const cartCounts=new Map(cart.items.map(item=>[Number(item.variant_id),Number(item.quantity)]));
      (container.funtoVariants||[]).forEach(v=>v.cart=cartCounts.get(Number(v.id))||0);
      const quantityInput=document.getElementById('Quantity-'+section);quantityInput.dataset.cartQuantity=String(cartCounts.get(id)||0);
      if(drawer?.renderContents){drawer.renderContents(result)}else window.location.assign(root+'cart');
    }catch(failure){error.textContent=failure.message||config.unavailable;error.hidden=false}
    finally{button.disabled=false;button.classList.remove('loading');button.removeAttribute('aria-disabled');scheduleOffers()}
  },true);
  const start = () => {
    updateOffers();
    enqueue(async () => {
      const cart = await readCart();
      if (!cart.items.some(managed)) return;
      const result = await reconcile(cart);
      if (result.changed) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function') {
          const sections = await sectionsFor(drawer.getSectionsToRender().map(section => section.id));
          drawer.renderContents({ ...result.cart, sections });
        }
      }
    }).catch(() => message(true));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();


